export type FreesoundCollection = {
  id: string
  name: string
  description: string
  itemCount: number
  thumbnails: [string, string, string]
}

export type FreesoundSound = {
  id: string
  name: string
  creator: string
  duration: number
  type: string
  license: string
  tags: string[]
  collectionIds: string[]
  preview: string
}

export type PodcastCollection = {
  id: string
  name: string
  description: string
  itemCount: number
  source: 'rss' | 'manual'
  feedUrl?: string
  lastSync?: string
}

export type PodcastEpisode = {
  id: string
  title: string
  show: string
  duration: number
  published: string
  hasTranscript: boolean
  hasSummary: boolean
  isPublished: boolean
  collectionIds: string[]
}

export type YouTubeCollection = {
  id: string
  name: string
  description: string
  itemCount: number
  source: 'channel' | 'manual' | 'empty'
  channelName?: string
  channelHandle?: string
  lastSync?: string
}

export type YouTubeVideo = {
  id: string
  title: string
  channel: string
  duration: number
  views: number
  published: string
  hasTranscript: boolean
  questionCount: number
  collectionIds: string[]
  thumbnail: string
}

export type RiveCollection = {
  id: string
  name: string
  description: string
  itemCount: number
}

export type RiveFile = {
  id: string
  name: string
  sizeKb: number
  eventCount: number
  useCase: 'yoga' | 'viseme' | 'illustration'
  collectionIds: string[]
  hasTimeline: boolean
  isPublished: boolean
}

export const freesoundCollections: FreesoundCollection[] = [
  {
    id: 'fc1',
    name: 'Cardiac Auscultation',
    description: 'Heart murmurs and normal heart sounds',
    itemCount: 24,
    thumbnails: [
      'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=100&q=80',
      'https://images.unsplash.com/photo-1559757175-5700dde675bc?w=100&q=80',
      'https://images.unsplash.com/photo-1530497610245-94d3c16cda28?w=100&q=80',
    ],
  },
  {
    id: 'fc2',
    name: 'Respiratory Sounds',
    description: 'Wheezes, crackles, and normal breath sounds',
    itemCount: 18,
    thumbnails: [
      'https://images.unsplash.com/photo-1584515933487-779824d29309?w=100&q=80',
      'https://images.unsplash.com/photo-1584036561566-baf8f5f1b144?w=100&q=80',
      'https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=100&q=80',
    ],
  },
  {
    id: 'fc3',
    name: 'Bowel Sounds',
    description: 'Normal and abnormal gastrointestinal sounds',
    itemCount: 12,
    thumbnails: [
      'https://images.unsplash.com/photo-1600508774634-4e11d34730e2?w=100&q=80',
      'https://images.unsplash.com/photo-1532938911079-1b06ac7ceec7?w=100&q=80',
      'https://images.unsplash.com/photo-1511174511562-5f7f18e874f8?w=100&q=80',
    ],
  },
  {
    id: 'fc4',
    name: 'Hospital Ambience',
    description: 'Background noise for simulation scenarios',
    itemCount: 45,
    thumbnails: [
      'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=100&q=80',
      'https://images.unsplash.com/photo-1586773860418-d37222d8fce3?w=100&q=80',
      'https://images.unsplash.com/photo-1551076805-e1869033e561?w=100&q=80',
    ],
  },
  {
    id: 'fc5',
    name: 'Medical Equipment',
    description: 'Monitors, ventilators, and alarms',
    itemCount: 32,
    thumbnails: [
      'https://images.unsplash.com/photo-1516549655169-df83a0774514?w=100&q=80',
      'https://images.unsplash.com/photo-1581594693702-fbdc51b2763b?w=100&q=80',
      'https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?w=100&q=80',
    ],
  },
]

export const freesoundSounds: FreesoundSound[] = [
  {
    id: 'fs1',
    name: 'Aortic Stenosis Murmur',
    creator: 'medsounds',
    duration: 15.4,
    type: 'wav',
    license: 'CC-BY',
    tags: ['cardiac', 'murmur', 'systolic'],
    collectionIds: ['fc1'],
    preview: 'https://example.com/audio1.mp3',
  },
  {
    id: 'fs2',
    name: 'Mitral Regurgitation',
    creator: 'dr_smith',
    duration: 12.1,
    type: 'mp3',
    license: 'CC0',
    tags: ['cardiac', 'murmur', 'pansystolic'],
    collectionIds: ['fc1'],
    preview: 'https://example.com/audio2.mp3',
  },
  {
    id: 'fs3',
    name: 'Normal S1 S2',
    creator: 'medsounds',
    duration: 20.0,
    type: 'wav',
    license: 'CC-BY',
    tags: ['cardiac', 'normal'],
    collectionIds: ['fc1'],
    preview: 'https://example.com/audio3.mp3',
  },
  {
    id: 'fs4',
    name: 'Coarse Crackles',
    creator: 'pulm_tech',
    duration: 18.5,
    type: 'wav',
    license: 'CC-BY-NC',
    tags: ['respiratory', 'crackles', 'pneumonia'],
    collectionIds: ['fc2'],
    preview: 'https://example.com/audio4.mp3',
  },
  {
    id: 'fs5',
    name: 'Expiratory Wheeze',
    creator: 'pulm_tech',
    duration: 14.2,
    type: 'mp3',
    license: 'CC0',
    tags: ['respiratory', 'wheeze', 'asthma'],
    collectionIds: ['fc2'],
    preview: 'https://example.com/audio5.mp3',
  },
  {
    id: 'fs6',
    name: 'Hyperactive Bowel',
    creator: 'gi_sounds',
    duration: 30.0,
    type: 'wav',
    license: 'CC-BY',
    tags: ['gi', 'bowel', 'hyperactive'],
    collectionIds: ['fc3'],
    preview: 'https://example.com/audio6.mp3',
  },
  {
    id: 'fs7',
    name: 'ICU Monitor Alarm',
    creator: 'sim_lab',
    duration: 5.5,
    type: 'wav',
    license: 'CC0',
    tags: ['equipment', 'alarm', 'icu'],
    collectionIds: ['fc4', 'fc5'],
    preview: 'https://example.com/audio7.mp3',
  },
  {
    id: 'fs8',
    name: 'Ventilator Cycle',
    creator: 'sim_lab',
    duration: 10.0,
    type: 'mp3',
    license: 'CC-BY',
    tags: ['equipment', 'ventilator'],
    collectionIds: ['fc5'],
    preview: 'https://example.com/audio8.mp3',
  },
]

export const podcastCollections: PodcastCollection[] = [
  {
    id: 'pc1',
    name: 'Clinical Rounds Podcast',
    description:
      'Weekly case-based discussions covering diagnosis, management, and clinical reasoning across specialties.',
    itemCount: 42,
    source: 'rss',
    feedUrl: 'https://feeds.clinicalrounds.org/podcast.xml',
    lastSync: '2026-04-14T10:30:00Z',
  },
  {
    id: 'pc2',
    name: 'MedEd Daily',
    description:
      'Bite-sized medical education content delivered every weekday morning.',
    itemCount: 218,
    source: 'rss',
    feedUrl: 'https://feeds.mededdaily.com/rss',
    lastSync: '2026-04-14T06:00:00Z',
  },
  {
    id: 'pc3',
    name: 'Emergency Medicine Picks',
    description: 'Hand-picked episodes from across the EM podcast landscape.',
    itemCount: 15,
    source: 'manual',
  },
  {
    id: 'pc4',
    name: 'Wellness Shortlist',
    description:
      'Curated episodes on physician wellness, burnout prevention, and resilience.',
    itemCount: 8,
    source: 'manual',
  },
]

export const podcastEpisodes: PodcastEpisode[] = [
  {
    id: 'pe1',
    title: 'Acute Chest Pain: A Systematic Approach',
    show: 'Clinical Rounds Podcast',
    duration: 3480,
    published: '2026-04-10T12:00:00Z',
    hasTranscript: true,
    hasSummary: true,
    isPublished: false,
    collectionIds: ['pc1', 'pc3'],
  },
  {
    id: 'pe2',
    title: 'Pediatric Fever Without a Source',
    show: 'Clinical Rounds Podcast',
    duration: 2640,
    published: '2026-04-03T12:00:00Z',
    hasTranscript: true,
    hasSummary: false,
    isPublished: true,
    collectionIds: ['pc1'],
  },
  {
    id: 'pe3',
    title: 'Spaced Repetition in Medical Education',
    show: 'MedEd Daily',
    duration: 1080,
    published: '2026-04-12T05:00:00Z',
    hasTranscript: true,
    hasSummary: true,
    isPublished: true,
    collectionIds: ['pc2'],
  },
  {
    id: 'pe4',
    title: 'Teaching Clinical Reasoning to Juniors',
    show: 'MedEd Daily',
    duration: 960,
    published: '2026-04-11T05:00:00Z',
    hasTranscript: false,
    hasSummary: false,
    isPublished: false,
    collectionIds: ['pc2'],
  },
  {
    id: 'pe5',
    title: 'RSI in the Community ED',
    show: 'EM Over Easy',
    duration: 2400,
    published: '2026-03-28T14:00:00Z',
    hasTranscript: true,
    hasSummary: true,
    isPublished: true,
    collectionIds: ['pc3'],
  },
  {
    id: 'pe6',
    title: 'Preventing Burnout in Residency',
    show: 'The Wellness Collective',
    duration: 1920,
    published: '2026-03-15T09:00:00Z',
    hasTranscript: true,
    hasSummary: true,
    isPublished: false,
    collectionIds: ['pc4'],
  },
]

export const youtubeCollections: YouTubeCollection[] = [
  {
    id: 'yc1',
    name: 'Medical School Lectures',
    description:
      'Comprehensive lectures covering core medical school curriculum',
    itemCount: 128,
    source: 'channel',
    channelName: 'Med School Insiders',
    channelHandle: '@MedSchoolInsiders',
    lastSync: '2026-04-14T06:00:00Z',
  },
  {
    id: 'yc2',
    name: 'Surgical Techniques',
    description: 'Detailed walkthroughs of common surgical procedures',
    itemCount: 64,
    source: 'channel',
    channelName: 'Ninja Nerd Science',
    channelHandle: '@NinjaNerdScience',
    lastSync: '2026-04-13T22:00:00Z',
  },
  {
    id: 'yc3',
    name: 'Emergency Medicine Cases',
    description: 'High-yield emergency medicine case studies and management',
    itemCount: 22,
    source: 'manual',
  },
  {
    id: 'yc4',
    name: 'Anatomy Deep Dives',
    description: 'In-depth exploration of human anatomy and physiology',
    itemCount: 15,
    source: 'manual',
  },
  {
    id: 'yc5',
    name: 'Empty (getting started)',
    description: 'A new collection ready for videos',
    itemCount: 0,
    source: 'empty',
  },
]

export const youtubeVideos: YouTubeVideo[] = [
  {
    id: 'yv1',
    title: 'Understanding the Cardiac Cycle',
    channel: 'Med School Insiders',
    duration: 845,
    views: 1250000,
    published: '2023-05-12T00:00:00Z',
    hasTranscript: true,
    questionCount: 5,
    collectionIds: ['yc1'],
    thumbnail: '',
  },
  {
    id: 'yv2',
    title: 'Renal Physiology Made Easy',
    channel: 'Med School Insiders',
    duration: 620,
    views: 850000,
    published: '2023-08-20T00:00:00Z',
    hasTranscript: true,
    questionCount: 3,
    collectionIds: ['yc1'],
    thumbnail: '',
  },
  {
    id: 'yv3',
    title: 'Laparoscopic Appendectomy Step-by-Step',
    channel: 'Ninja Nerd Science',
    duration: 1250,
    views: 2100000,
    published: '2022-11-15T00:00:00Z',
    hasTranscript: true,
    questionCount: 8,
    collectionIds: ['yc2'],
    thumbnail: '',
  },
  {
    id: 'yv4',
    title: 'Basic Suturing Techniques',
    channel: 'Ninja Nerd Science',
    duration: 680,
    views: 1800000,
    published: '2022-12-05T00:00:00Z',
    hasTranscript: true,
    questionCount: 6,
    collectionIds: ['yc2'],
    thumbnail: '',
  },
  {
    id: 'yv5',
    title: 'Approach to Acute Chest Pain',
    channel: 'EM Cases',
    duration: 920,
    views: 1500000,
    published: '2023-01-10T00:00:00Z',
    hasTranscript: true,
    questionCount: 10,
    collectionIds: ['yc3'],
    thumbnail: '',
  },
  {
    id: 'yv6',
    title: 'Management of Status Epilepticus',
    channel: 'EM Cases',
    duration: 1200,
    views: 45000,
    published: '2024-02-18T00:00:00Z',
    hasTranscript: false,
    questionCount: 0,
    collectionIds: ['yc3'],
    thumbnail: '',
  },
  {
    id: 'yv7',
    title: 'Brachial Plexus Anatomy',
    channel: 'AnatomyZone',
    duration: 850,
    views: 62000,
    published: '2024-03-22T00:00:00Z',
    hasTranscript: true,
    questionCount: 4,
    collectionIds: ['yc4'],
    thumbnail: '',
  },
  {
    id: 'yv8',
    title: 'Circle of Willis',
    channel: 'AnatomyZone',
    duration: 1500,
    views: 320000,
    published: '2025-01-05T00:00:00Z',
    hasTranscript: true,
    questionCount: 2,
    collectionIds: ['yc4'],
    thumbnail: '',
  },
]

export const riveCollections: RiveCollection[] = [
  {
    id: 'rc1',
    name: 'Yoga & Rehabilitation',
    description: 'Therapeutic yoga pose sequences',
    itemCount: 6,
  },
  {
    id: 'rc2',
    name: 'Patient Education Visemes',
    description: 'Mouth-sync animations for patient instructions',
    itemCount: 4,
  },
  {
    id: 'rc3',
    name: 'Anatomy Animations',
    description: 'Anatomical illustration animations',
    itemCount: 3,
  },
]

export const riveFiles: RiveFile[] = [
  {
    id: 'rf1',
    name: 'sun-salutation.riv',
    sizeKb: 380,
    eventCount: 8,
    useCase: 'yoga',
    collectionIds: ['rc1'],
    hasTimeline: true,
    isPublished: true,
  },
  {
    id: 'rf2',
    name: 'hip-flexor-stretch.riv',
    sizeKb: 290,
    eventCount: 5,
    useCase: 'yoga',
    collectionIds: ['rc1'],
    hasTimeline: true,
    isPublished: false,
  },
  {
    id: 'rf3',
    name: 'medication-instructions-en.riv',
    sizeKb: 720,
    eventCount: 54,
    useCase: 'viseme',
    collectionIds: ['rc2'],
    hasTimeline: true,
    isPublished: true,
  },
  {
    id: 'rf4',
    name: 'post-op-care-instructions.riv',
    sizeKb: 680,
    eventCount: 48,
    useCase: 'viseme',
    collectionIds: ['rc2'],
    hasTimeline: false,
    isPublished: false,
  },
  {
    id: 'rf5',
    name: 'cardiac-cycle-illustration.riv',
    sizeKb: 450,
    eventCount: 12,
    useCase: 'illustration',
    collectionIds: ['rc1', 'rc3'],
    hasTimeline: true,
    isPublished: true,
  },
  {
    id: 'rf6',
    name: 'respiratory-illustration.riv',
    sizeKb: 520,
    eventCount: 10,
    useCase: 'illustration',
    collectionIds: ['rc1', 'rc3'],
    hasTimeline: true,
    isPublished: false,
  },
]
